import React, { useState } from 'react';
import { resetPassword } from '../../firebase/auth';

export default function ForgotPasswordForm({ onBack, loading, setLoading }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const { error: resetError } = await resetPassword(email);

      if (resetError) {
        let errorMessage = 'Failed to send reset email. Please try again.';

        switch (resetError.code) {
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email address';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many requests. Please wait before trying again.';
            break;
          default:
            errorMessage = resetError.message || errorMessage;
        }

        setError(errorMessage);
      } else {
        setSuccess(true);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ padding: '40px 30px 30px', textAlign: 'center' }}>
        <div style={{
          backgroundColor: 'var(--success-bg, #d4edda)',
          border: '1px solid var(--success-border, #c3e6cb)',
          borderRadius: '6px',
          padding: '20px',
          marginBottom: '30px',
          color: 'var(--success-text, #155724)'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: 'var(--success-text, #155724)' }}>Email Sent!</h3>
          <p style={{ margin: 0, lineHeight: '1.5' }}>
            We've sent a password reset link to <strong>{email}</strong>.
            Please check your email and follow the instructions to reset your password.
          </p>
        </div>

        <button
          onClick={onBack}
          disabled={loading}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--btn-primary)',
            color: 'var(--btn-text, #fff)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Back to Sign In
        </button>

        <div style={{ marginTop: '20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
          <p>Didn't receive the email? Check your spam folder or{' '}
            <button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--btn-primary)',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px'
              }}
            >
              try again
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 30px 30px' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h2 style={{
          margin: '0 0 10px 0',
          color: 'var(--text-primary)',
          fontSize: '28px',
          fontWeight: 'bold'
        }}>
          Reset Password
        </h2>
        <p style={{
          margin: 0,
          color: 'var(--text-secondary)',
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--danger-bg, #fee)',
          border: '1px solid var(--danger-border, #fcc)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '20px',
          color: 'var(--danger)',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}>
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${error ? 'var(--danger)' : 'var(--border-color)'}`,
              borderRadius: '6px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s',
              opacity: loading ? 0.7 : 1
            }}
            placeholder="Enter your email"
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: loading ? 'var(--btn-disabled, #ccc)' : 'var(--btn-primary)',
            color: 'var(--btn-text, #fff)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '20px',
            transition: 'background-color 0.2s'
          }}
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </button>

        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--btn-primary)',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              textDecoration: 'underline',
              opacity: loading ? 0.5 : 1
            }}
          >
            Back to Sign In
          </button>
        </div>
      </form>
    </div>
  );
}